import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invalidateSiteStaticCache } from './queries/useSiteStaticData';
import { logger } from '@/lib/logger';
export type WorkItemType =
  | 'installation_chantier'
  | 'gros_beton'
  | 'massifs'
  | 'semelles_isolees'
  | 'semelles_filantes'
  | 'longrines'
  | 'radier'
  | 'dallage'
  | 'bassin'
  | 'voiles_ext'
  | 'voiles_int'
  | 'poteaux'
  | 'poutres'
  | 'plancher'
  | 'balcons'
  | 'acroteres'
  | 'releves'
  | 'escaliers'
  | 'soutenement'
  | 'muret'
  | 'autres';

export const WORK_ITEM_LABELS: Record<WorkItemType, string> = {
  installation_chantier: 'Installation de chantier',
  gros_beton: 'Gros béton',
  massifs: 'Massifs',
  semelles_isolees: 'Semelles isolées',
  semelles_filantes: 'Semelles filantes',
  longrines: 'Longrines',
  radier: 'Radier',
  dallage: 'Dallage',
  bassin: 'Bassin',
  voiles_ext: 'Voiles extérieurs',
  voiles_int: 'Voiles intérieurs',
  poteaux: 'Poteaux',
  poutres: 'Poutres',
  plancher: 'Plancher',
  balcons: 'Balcons',
  acroteres: 'Acrotères',
  releves: 'Relevés',
  escaliers: 'Escaliers',
  soutenement: 'Soutènement',
  muret: 'Muret',
  autres: 'Autres',
};

// Preset des ouvrages par défaut pour tous les nouveaux chantiers
export const DEFAULT_WORK_ITEMS_PRESET: CustomWorkItem[] = [
  { id: 'installation_chantier', name: 'Installation de chantier' },
  { id: 'gros_beton', name: 'Gros béton' },
  { id: 'massifs', name: 'Massifs' },
  { id: 'semelles_isolees', name: 'Semelles isolées' },
  { id: 'semelles_filantes', name: 'Semelles filantes' },
  { id: 'longrines', name: 'Longrines' },
  { id: 'radier', name: 'Radier' },
  { id: 'dallage', name: 'Dallage' },
  { id: 'bassin', name: 'Bassin' },
  { id: 'voiles_ext', name: 'Voiles extérieurs' },
  { id: 'voiles_int', name: 'Voiles intérieurs' },
  { id: 'poteaux', name: 'Poteaux' },
  { id: 'poutres', name: 'Poutres' },
  { id: 'plancher', name: 'Plancher' },
  { id: 'balcons', name: 'Balcons' },
  { id: 'acroteres', name: 'Acrotères' },
  { id: 'releves', name: 'Relevés' },
  { id: 'escaliers', name: 'Escaliers' },
  { id: 'soutenement', name: 'Soutènement' },
  { id: 'muret', name: 'Muret' }
];

export interface WorkItemPrevis {
  id?: string;
  site_id: string;
  building: string;
  level: string;
  name: string;
  volume: number;
}

export interface CustomWorkItem {
  id: string;
  name: string;
}

export interface BuildingData {
  name: string;
  minLevel: number;
  maxLevel: number;
  levels: string[];
  volumes: Record<string, Record<string, number>>; // level -> workItemId -> volume
  customWorkItems: CustomWorkItem[]; // ouvrages personnalisés pour ce bâtiment
}

// Fonction de fetch séparée pour React Query
const fetchPrevisionnelData = async (chantierId: string) => {
  // Charger les work items triés par display_order
  const { data: ouvragesData, error: ouvragesError } = await supabase
    .from('work_items')
    .select('id, site_id, building, level, name, volume, display_order, start_date, estimated_end_date, created_at')
    .eq('site_id', chantierId)
    .order('display_order', { ascending: true })
    .limit(200);

  if (ouvragesError) throw ouvragesError;

  // Charger les work items verrouillés (avec béton coulé)
  const { data: workItemsWithPours } = await supabase
    .from('work_items')
    .select(`
      id,
      building,
      level,
      name,
      pour_work_items(id),
      delivery_work_items(id)
    `)
    .eq('site_id', chantierId);

  // Créer un Set des IDs verrouillés
  const lockedWorkItems = new Set<string>(
    workItemsWithPours
      ?.filter(wi =>
        (wi.pour_work_items && wi.pour_work_items.length > 0) ||
        (wi.delivery_work_items && wi.delivery_work_items.length > 0)
      )
      .map(wi => `${wi.building}-${wi.level}-${wi.name}`) || []
  );

  // Transformer en structure BuildingData
  const buildingMap: Record<string, BuildingData> = {};

  ouvragesData?.forEach(workItem => {
    const buildingName = workItem.building;

    if (!buildingMap[buildingName]) {
      buildingMap[buildingName] = {
        name: buildingName,
        minLevel: Infinity,
        maxLevel: -Infinity,
        levels: [],
        volumes: {},
        customWorkItems: []
      };
    }

    const building = buildingMap[buildingName];
    const level = workItem.level;
    const workItemId = workItem.name;
    const workItemName = workItem.name;

    // Calculer les niveaux min/max réels
    const levelNum = parseInt(level);
    if (!isNaN(levelNum)) {
      building.minLevel = Math.min(building.minLevel, levelNum);
      building.maxLevel = Math.max(building.maxLevel, levelNum);
    }

    // Ajouter l'ouvrage s'il n'existe pas déjà
    if (!building.customWorkItems.find(o => o.id === workItemId)) {
      building.customWorkItems.push({
        id: workItemId,
        name: workItemName
      });
    }

    if (!building.volumes[level]) {
      building.volumes[level] = {};
    }

    building.volumes[level][workItemId] = workItem.volume || 0;
  });

  // Générer une liste de niveaux entre deux bornes
  const generateLevels = (min: number, max: number): string[] => {
    const levels: string[] = [];
    for (let i = min; i <= max; i++) {
      levels.push(i.toString());
    }
    return levels;
  };

  // Générer les niveaux
  Object.values(buildingMap).forEach(building => {
    if (building.minLevel === Infinity) {
      building.minLevel = -2;
      building.maxLevel = 5;
    }
    building.levels = generateLevels(building.minLevel, building.maxLevel);
  });

  return {
    buildings: Object.values(buildingMap),
    lockedWorkItems
  };
};

export const useSitePrevisionnel = (chantierId: string) => {
  const [buildings, setBuildings] = useState<BuildingData[]>([]);
  const [saving, setSaving] = useState(false);
  const [lockedWorkItems, setLockedWorkItems] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const hasLoadedRef = useRef(false);
  const hasPendingChangesRef = useRef(false);
  const displayedToastsRef = useRef<Set<string>>(new Set());
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Historique pour undo/redo
  const [history, setHistory] = useState<BuildingData[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // React Query pour le chargement initial
  const { data: queryData, isLoading: loading, isFetching } = useQuery({
    queryKey: ['site-previsionnel', chantierId],
    queryFn: () => fetchPrevisionnelData(chantierId),
    enabled: !!chantierId,
    staleTime: 5 * 60 * 1000,       // 5 min - previsionnel change rarement
    gcTime: 30 * 60 * 1000,         // 30 min
    refetchOnMount: false,          // Utiliser cache
    refetchOnWindowFocus: false,    // Pas de refetch auto
  });

  // Référence pour stocker le hash des dernières données chargées
  const lastDataHashRef = useRef<string | null>(null);

  // Fonction utilitaire pour générer un hash simple des données
  const hashBuildings = useCallback((buildings: BuildingData[]) => {
    return JSON.stringify(buildings.map(b => ({
      name: b.name,
      minLevel: b.minLevel,
      maxLevel: b.maxLevel,
      volumes: b.volumes,
      customWorkItems: b.customWorkItems.map(o => o.id)
    })));
  }, []);

  // Synchroniser les données React Query avec l'état local
  useEffect(() => {
    if (!queryData) return;

    const newHash = hashBuildings(queryData.buildings);

    // Ne resync que si:
    // 1. Premier chargement (hasLoadedRef false)
    // 2. Pas de changements locaux en attente ET les données serveur ont changé
    const shouldResync = !hasLoadedRef.current ||
      (!hasPendingChangesRef.current && lastDataHashRef.current !== newHash);

    if (shouldResync) {
      setBuildings(queryData.buildings);
      setLockedWorkItems(queryData.lockedWorkItems);
      displayedToastsRef.current.clear();
      setHistory([structuredClone(queryData.buildings)]);
      setHistoryIndex(0);
      hasLoadedRef.current = true;
      lastDataHashRef.current = newHash;
    }
  }, [queryData, hashBuildings]);

  // Générer une liste de niveaux entre deux bornes
  const generateLevels = (min: number, max: number): string[] => {
    const levels: string[] = [];
    for (let i = min; i <= max; i++) {
      levels.push(i.toString());
    }
    return levels;
  };


  // Fonction supprimée (dead code)

  // Sauvegarder les modifications avec UPSERT intelligent
  const saveDataInternal = useCallback(async (): Promise<boolean> => {
    if (!chantierId) return false;

    setSaving(true);
    try {
      // Récupérer tous les work items existants pour ce chantier
      const { data: existingOuvrages, error: fetchError } = await supabase
        .from('work_items')
        .select('id, building, level, name')
        .eq('site_id', chantierId);

      if (fetchError) throw fetchError;

      // Récupérer les work_items qui ont déjà du béton coulé
      const { data: workItemsWithPours } = await supabase
        .from('work_items')
        .select(`
          id,
          name,
          pour_work_items(id),
          delivery_work_items(id)
        `)
        .eq('site_id', chantierId);

      // Créer un Set des IDs qui ne peuvent pas être modifiés
      const lockedWorkItemIds = new Set<string>(
        workItemsWithPours
          ?.filter(wi =>
            (wi.pour_work_items && wi.pour_work_items.length > 0) ||
            (wi.delivery_work_items && wi.delivery_work_items.length > 0)
          )
          .map(wi => wi.id) || []
      );

      // Map pour récupérer les noms des ouvrages verrouillés
      const lockedWorkItemNames = new Map<string, string>(
        workItemsWithPours
          ?.filter(wi => lockedWorkItemIds.has(wi.id))
          .map(wi => [wi.id, wi.name]) || []
      );

      // Tracker les IDs des work items à conserver
      const idsToKeep = new Set<string>();

      let displayOrderCounter = 0;

      for (const building of buildings) {
        // Filtrer les ouvrages qui ont au moins un volume > 0
        const workItemsWithVolumes = building.customWorkItems.filter(workItem => {
          return building.levels.some(level => {
            const volume = building.volumes[level]?.[workItem.id] || 0;
            return volume > 0;
          });
        });

        // Si un bâtiment n'a aucun ouvrage avec volume, créer un work_item factice pour l'ancrer
        if (workItemsWithVolumes.length === 0 && building.customWorkItems.length > 0) {
          const firstWorkItem = building.customWorkItems[0];
          const firstLevel = building.levels[0];

          displayOrderCounter++;

          // Chercher si ce work item existe déjà
          const existing = existingOuvrages?.find(
            e => e.building === building.name &&
                 e.level === firstLevel &&
                 e.name === firstWorkItem.name
          );

          if (existing) {
            idsToKeep.add(existing.id);
            const { error: updateError } = await supabase
              .from('work_items')
              .update({
                volume: 0,
                display_order: displayOrderCounter,
              })
              .eq('id', existing.id);

            if (updateError) throw updateError;
          } else {
            const { data: inserted, error: insertError } = await supabase
              .from('work_items')
              .insert({
                site_id: chantierId,
                building: building.name,
                level: firstLevel,
                name: firstWorkItem.name,
                volume: 0,
                display_order: displayOrderCounter,
              })
              .select('id')
              .single();

            if (insertError) throw insertError;
            if (inserted) idsToKeep.add(inserted.id);
          }
        }

        for (const workItem of workItemsWithVolumes) {
          for (const level of building.levels) {
            displayOrderCounter++; // Incrémenter DANS la boucle niveau pour éviter les doublons
            const volume = building.volumes[level]?.[workItem.id] || 0;

            // Ne sauvegarder que les work items avec un volume > 0
            if (volume <= 0) continue;

            // Chercher si ce work item existe déjà
            const existing = existingOuvrages?.find(
              e => e.building === building.name &&
                   e.level === level &&
                   e.name === workItem.name
            );

            if (existing) {
              // UPDATE
              idsToKeep.add(existing.id);

              // Sauter les ouvrages verrouillés (béton déjà coulé) - pas de toast car l'UI bloque déjà
              if (lockedWorkItemIds.has(existing.id)) {
                continue;
              }

              const { error: updateError } = await supabase
                .from('work_items')
                .update({
                  volume,
                  display_order: displayOrderCounter,
                })
                .eq('id', existing.id);

              if (updateError) throw updateError;
            } else {
              // INSERT
              const { data: inserted, error: insertError } = await supabase
                .from('work_items')
                .insert({
                  site_id: chantierId,
                  building: building.name,
                  level: level,
                  name: workItem.name,
                  volume,
                  display_order: displayOrderCounter,
                })
                .select('id')
                .single();

              if (insertError) throw insertError;
              if (inserted) idsToKeep.add(inserted.id);
            }
          }
        }
      }

      // Supprimer les work items qui n'existent plus
      // Mais ne pas supprimer ceux qui ont du béton coulé
      const idsToDelete = existingOuvrages
        ?.filter(e => !idsToKeep.has(e.id) && !lockedWorkItemIds.has(e.id))
        .map(e => e.id) || [];

      // Vérifier si des work_items verrouillés allaient être supprimés
      const blockedDeletions = existingOuvrages
        ?.filter(e => !idsToKeep.has(e.id) && lockedWorkItemIds.has(e.id)) || [];

      if (blockedDeletions.length > 0) {
        const ouvrageNames = blockedDeletions
          .map(e => lockedWorkItemNames.get(e.id) || e.name)
          .join(', ');

        toast.error("Suppression impossible", { description: `Impossible de supprimer : ${ouvrageNames}. Du béton a déjà été coulé dans ${blockedDeletions.length > 1 ? 'ces ouvrages' : 'cet ouvrage'}.` });
      }

      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('work_items')
          .delete()
          .in('id', idsToDelete);

        if (deleteError) throw deleteError;
      }

      // Nettoyer le Set des toasts affichés après sauvegarde réussie
      displayedToastsRef.current.clear();

      // Étape 1: Tenter d'invalider le cache serveur (Edge Function) pour synchroniser tous les utilisateurs
      const cacheInvalidated = await invalidateSiteStaticCache(chantierId);

      // Étape 2: Invalider les caches React Query locaux
      queryClient.invalidateQueries({ queryKey: ['site-static-data', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['site-previsionnel', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['site-budget-fresh', chantierId] });

      // Étape 3: Si l'invalidation serveur a échoué, forcer un refetch immédiat
      if (!cacheInvalidated) {
        logger.warn("useSitePrevisionnel", "Server cache invalidation failed, forcing refetch with fresh data");
        // Effacer les données en cache pour forcer un fetch frais
        queryClient.setQueryData(['site-static-data', chantierId], undefined);
        // Refetch immédiatement (utilisera le fallback direct vers Supabase)
        queryClient.refetchQueries({ queryKey: ['site-static-data', chantierId] });
      }

      // Marquer qu'il n'y a plus de changements en attente
      hasPendingChangesRef.current = false;

      return true;
    } catch (error: any) {
      logger.debug("useSitePrevisionnel", "Erreur sauvegarde prévisionnel:", error);

      // Identifier le type de contrainte CHECK
      const isConstraintError = error.message?.includes('violates check constraint') || error.code === '23514';

      if (isConstraintError) {
        // Ignorer uniquement les erreurs de contrainte de volume (valeurs intermédiaires pendant édition)
        const isVolumeConstraint = error.message?.includes('volume');

        if (isVolumeConstraint) {
          return false;
        }

        // Afficher une erreur explicite pour les autres contraintes
        toast.error("Erreur de sauvegarde", { description: "Une contrainte de données n'a pas été respectée. Vérifiez vos entrées." });
        return false;
      }

      // Si c'est une erreur réseau, réessayer automatiquement
      if (error.message?.toLowerCase().includes('fetch') || error.message?.toLowerCase().includes('network')) {
        toast.error("Erreur réseau", { description: "Nouvelle tentative dans 3 secondes..." });

        // Retry après 3 secondes
        setTimeout(() => saveData(), 3000);
        return false;
      }

      toast.error("Erreur", { description: `Impossible de sauvegarder: ${error.message}` });
      return false;
    } finally {
      setSaving(false);
    }
  }, [chantierId, buildings, toast]);

  // Wrapper pour appeler saveDataInternal
  const saveData = useCallback(async (): Promise<boolean> => {
    return await saveDataInternal();
  }, [saveDataInternal]);

  // Ajouter un bâtiment
  const addBuilding = (name: string) => {
    const newBuilding: BuildingData = {
      name,
      minLevel: -2,
      maxLevel: 5,
      levels: generateLevels(-2, 5),
      volumes: {},
      customWorkItems: [...DEFAULT_WORK_ITEMS_PRESET] // Initialiser avec tous les ouvrages preset
    };

    setBuildings(prev => {
      const newState = [...prev, newBuilding];
      // Sauvegarder avec le nouvel état directement
      const currentState = structuredClone(newState);
      setHistory(h => {
        const newHistory = h.slice(0, historyIndex + 1);
        newHistory.push(currentState);
        return newHistory.slice(-50);
      });
      setHistoryIndex(i => i + 1);
      return newState;
    });
  };

  // Dupliquer un bâtiment existant
  const duplicateBuilding = (sourceName: string, newName: string) => {
    const sourceBuilding = buildings.find(b => b.name === sourceName);
    if (!sourceBuilding) {
      toast.error("Erreur", { description: `Bâtiment "${sourceName}" introuvable` });
      return;
    }

    // Vérifier que le nouveau nom n'existe pas déjà
    if (buildings.some(b => b.name === newName)) {
      toast.error("Erreur", { description: `Un bâtiment "${newName}" existe déjà` });
      return;
    }

    const duplicatedBuilding: BuildingData = {
      name: newName,
      minLevel: sourceBuilding.minLevel,
      maxLevel: sourceBuilding.maxLevel,
      levels: [...sourceBuilding.levels],
      volumes: structuredClone(sourceBuilding.volumes),
      customWorkItems: structuredClone(sourceBuilding.customWorkItems)
    };

    setBuildings(prev => {
      const newState = [...prev, duplicatedBuilding];
      const currentState = structuredClone(newState);
      setHistory(h => {
        const newHistory = h.slice(0, historyIndex + 1);
        newHistory.push(currentState);
        return newHistory.slice(-50);
      });
      setHistoryIndex(i => i + 1);
      return newState;
    });

    toast.success("Bâtiment dupliqué", { description: `Le bâtiment "${newName}" a été créé avec les mêmes ouvrages et volumes que "${sourceName}"` });
  };

  // Supprimer un bâtiment
  const removeBuilding = (name: string) => {
    setBuildings(prev => {
      const newState = prev.filter(b => b.name !== name);
      // Sauvegarder avec le nouvel état directement
      const currentState = structuredClone(newState);
      setHistory(h => {
        const newHistory = h.slice(0, historyIndex + 1);
        newHistory.push(currentState);
        return newHistory.slice(-50);
      });
      setHistoryIndex(i => i + 1);
      return newState;
    });
  };

  // Définir la plage de niveaux pour un bâtiment
  const setLevelsRange = (buildingName: string, minLevel: number, maxLevel: number) => {
    setBuildings(prev => {
      const newState = prev.map(building => {
        if (building.name === buildingName && minLevel <= maxLevel) {
          const newLevels = generateLevels(minLevel, maxLevel);
          const newVolumes: Record<string, Record<string, number>> = {};

          // Conserver les volumes des niveaux qui existent toujours
          newLevels.forEach(level => {
            if (building.volumes[level]) {
              newVolumes[level] = building.volumes[level];
            }
          });

          return {
            ...building,
            minLevel,
            maxLevel,
            levels: newLevels,
            volumes: newVolumes
          };
        }
        return building;
      });

      // Sauvegarder avec le nouvel état directement
      const currentState = structuredClone(newState);
      setHistory(h => {
        const newHistory = h.slice(0, historyIndex + 1);
        newHistory.push(currentState);
        return newHistory.slice(-50);
      });
      setHistoryIndex(i => i + 1);
      return newState;
    });
  };

  // Ajouter un ouvrage personnalisé
  const addWorkItem = async (buildingName: string, designation: string) => {
    const newWorkItemId = designation;

    const newWorkItem: CustomWorkItem = {
      id: newWorkItemId,
      name: designation
    };

    setBuildings(prev => prev.map(building => {
      if (building.name === buildingName) {
        return {
          ...building,
          customWorkItems: [...building.customWorkItems, newWorkItem]
        };
      }
      return building;
    }));
  };

  // Supprimer un ouvrage personnalisé
  const removeWorkItem = (buildingName: string, workItemId: string) => {
    saveToHistory(); // Sauvegarder l'état pour undo/redo

    setBuildings(prev => prev.map(building => {
      if (building.name === buildingName) {
        const newVolumes = { ...building.volumes };
        // Supprimer les volumes de cet ouvrage
        Object.keys(newVolumes).forEach(level => {
          if (newVolumes[level][workItemId]) {
            delete newVolumes[level][workItemId];
          }
        });

        return {
          ...building,
          customWorkItems: building.customWorkItems.filter(o => o.id !== workItemId),
          volumes: newVolumes
        };
      }
      return building;
    }));
  };

  // Mettre à jour la désignation d'un ouvrage
  const updateWorkItemName = (buildingName: string, workItemId: string, newDesignation: string) => {
    setBuildings(prev => prev.map(building => {
      if (building.name === buildingName) {
        return {
          ...building,
          customWorkItems: building.customWorkItems.map(o =>
            o.id === workItemId ? { ...o, name: newDesignation } : o
          )
        };
      }
      return building;
    }));
  };

  // Sauvegarder l'état actuel dans l'historique (memoizé pour éviter clonage excessif)
  const saveToHistory = useCallback(() => {
    setHistory(h => {
      const currentState = structuredClone(buildings);
      const newHistory = h.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      return newHistory.slice(-50); // Harmonisé à 50 entrées max
    });
    setHistoryIndex(i => i + 1);
  }, [buildings, historyIndex]);

  // Annuler la dernière modification
  const undo = () => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      setBuildings(structuredClone(previousState));
      setHistoryIndex(historyIndex - 1);
    }
  };

  // Rétablir la dernière modification annulée
  const redo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setBuildings(structuredClone(nextState));
      setHistoryIndex(historyIndex + 1);
    }
  };

  // Mettre à jour le volume d'un ouvrage
  const updateVolume = useCallback((buildingName: string, level: string, workItemId: string, volume: number) => {
    saveToHistory();

    setBuildings(prev => {
      const newState = prev.map(building => {
        if (building.name === buildingName) {
          const newVolumes = { ...building.volumes };
          if (!newVolumes[level]) {
            newVolumes[level] = {};
          }
          newVolumes[level] = { ...newVolumes[level], [workItemId]: volume };

          return {
            ...building,
            volumes: newVolumes
          };
        }
        return building;
      });
      return newState;
    });
  }, [saveToHistory]);

  // Obtenir le volume d'un ouvrage
  const getVolume = (buildingName: string, level: string, workItemId: string): number => {
    const building = buildings.find(b => b.name === buildingName);
    return building?.volumes[level]?.[workItemId] || 0;
  };

  // Calculer le total d'un niveau pour un bâtiment
  const getLevelTotal = (buildingName: string, level: string): number => {
    const building = buildings.find(b => b.name === buildingName);
    if (!building || !building.volumes[level]) return 0;

    return Object.values(building.volumes[level]).reduce((total, volume) => total + volume, 0);
  };

  // Réorganiser les ouvrages dans un bâtiment
  const reorderWorkItems = (buildingName: string, oldIndex: number, newIndex: number) => {
    saveToHistory(); // Sauvegarder l'état pour undo/redo

    setBuildings(prev => prev.map(building => {
      if (building.name === buildingName) {
        const newWorkItems = [...building.customWorkItems];
        const [removed] = newWorkItems.splice(oldIndex, 1);
        newWorkItems.splice(newIndex, 0, removed);
        return {
          ...building,
          customWorkItems: newWorkItems
        };
      }
      return building;
    }));
  };

  // Calculer les totaux
  const getTotalVolume = (): number => {
    return buildings.reduce((total, building) => {
      return total + Object.values(building.volumes).reduce((buildingTotal, levelVolumes) => {
        return buildingTotal + Object.values(levelVolumes).reduce((levelTotal, volume) => levelTotal + volume, 0);
      }, 0);
    }, 0);
  };

  // Sauvegarder dans localStorage avec debounce (optimisation)
  useEffect(() => {
    if (buildings.length === 0 || loading || !hasLoadedRef.current) return;

    const draftKey = `previsionnel_draft_${chantierId}`;
    const timeoutId = setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify(buildings));
      hasPendingChangesRef.current = true;
    }, 500); // Debounce 500ms pour localStorage

    return () => clearTimeout(timeoutId);
  }, [buildings, chantierId, loading]);

  // Chargement géré par React Query

  // Vérifier si un ouvrage est verrouillé
  const isWorkItemLockedForBuilding = (buildingName: string, level: string, workItemName: string): boolean => {
    const key = `${buildingName}-${level}-${workItemName}`;
    return lockedWorkItems.has(key);
  };

  // Force refresh - invalide les caches et refetch les données fraîches
  const forceRefresh = useCallback(async (): Promise<boolean> => {
    if (!chantierId) return false;

    try {
      // Reset le flag de chargement pour permettre le resync
      hasLoadedRef.current = false;
      hasPendingChangesRef.current = false;

      // Invalider le cache serveur (Edge Function)
      await invalidateSiteStaticCache(chantierId);

      // Invalider tous les caches React Query liés
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['site-previsionnel', chantierId] }),
        queryClient.invalidateQueries({ queryKey: ['site-static-data', chantierId] }),
        queryClient.invalidateQueries({ queryKey: ['site-budget-fresh', chantierId] })
      ]);

      // Refetch les données
      await queryClient.refetchQueries({ queryKey: ['site-previsionnel', chantierId] });

      return true;
    } catch (error) {
      logger.error("useSitePrevisionnel", "Force refresh failed:", error);
      return false;
    }
  }, [chantierId, queryClient]);

  return {
    buildings,
    loading,
    isFetching,
    saving,
    addBuilding,
    duplicateBuilding,
    removeBuilding,
    setLevelsRange,
    addWorkItem,
    removeWorkItem,
    updateWorkItemName,
    updateVolume,
    getVolume,
    getLevelTotal,
    getTotalVolume,
    reorderWorkItems,
    saveData,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    isWorkItemLockedForBuilding,
    lockedWorkItems,
    forceRefresh
  };
};
